import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { PageHeader, SectionContent, StyledList } from '../../components';
import { ONLINE_PRIVACY_STATEMENT } from '../../data';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const OnlinePrivacyStatementPage = ({ setRefs }) => {
  const onlinePrivacyStatementRef = useRef(null);
  const recruitmentNoteRef = useRef(null);
  const ourRoleRef = useRef(null);
  const ourRoleAdditionalRef = useRef(null);
  const dataProtectionRef = useRef(null);
  const dataProtectionConclusionRef = useRef(null);
  const personalInfoCollectionRef = useRef(null);
  const personalInfoSpecialRef = useRef(null);
  const infoCollectionMethodRef = useRef(null);
  const infoUsageRef = useRef(null);
  const infoUsageSpecialRef = useRef(null);
  const automatedDecisionRef = useRef(null);
  const infoSharingRef = useRef(null);
  const infoSharingConclusionRef = useRef(null);
  const internationalTransfersRef = useRef(null);
  const infoRetentionRef = useRef(null);
  const userRightsRef = useRef(null);
  const userRightsContactRef = useRef(null);
  const noticeChangesRef = useRef(null);

  const refs = useMemo(
    () => ({
      onlinePrivacyStatement: onlinePrivacyStatementRef,
      recruitmentNote: recruitmentNoteRef,
      ourRole: ourRoleRef,
      ourRoleAdditional: ourRoleAdditionalRef,
      dataProtection: dataProtectionRef,
      dataProtectionConclusion: dataProtectionConclusionRef,
      personalInfoCollection: personalInfoCollectionRef,
      personalInfoSpecial: personalInfoSpecialRef,
      infoCollectionMethod: infoCollectionMethodRef,
      infoUsage: infoUsageRef,
      infoUsageSpecial: infoUsageSpecialRef,
      automatedDecision: automatedDecisionRef,
      infoSharing: infoSharingRef,
      infoSharingConclusion: infoSharingConclusionRef,
      internationalTransfers: internationalTransfersRef,
      infoRetention: infoRetentionRef,
      userRights: userRightsRef,
      userRightsContact: userRightsContactRef,
      noticeChanges: noticeChangesRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.onlinePrivacyStatement.title}
        content={seoData.onlinePrivacyStatement.content}
        keywords={seoData.onlinePrivacyStatement.keywords}
        canonical={seoData.onlinePrivacyStatement.canonical}
        ogImage={seoData.onlinePrivacyStatement.ogImage}
      />
      {/* Page Header */}
      <PageHeader
        id='hero'
        title={ONLINE_PRIVACY_STATEMENT.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />

      {/* Introduction */}
      <SectionContent
        id='onlinePrivacyStatement'
        ref={refs.onlinePrivacyStatement}
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.LAST_UPDATED,
            marginBottom: ELEMENT_SPACING,
            fontStyle: 'italic',
            fontSize: '1.1rem',
            color: THEME.COLORS.NEUTRAL_GRAY_DARK
          },
          ...ONLINE_PRIVACY_STATEMENT.INTRO.map(paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }))
        ]}
      />

      {/* Recruitment Note */}
      <SectionContent
        id='recruitmentNote' // Added ID
        ref={refs.recruitmentNote} // Added ref
        initialSpacing={false}
        linkMappings={ONLINE_PRIVACY_STATEMENT.LINKS.map(link => ({
          word: link.TEXT,
          url: link.URL,
          color: THEME.COLORS.PRIMARY_BLUE,
          textDecoration: 'underlined',
          newTab: false
        }))}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.RECRUITMENT_NOTE,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Our Role and When This Privacy Notice Applies section */}
      <SectionContent
        id='ourRole'
        ref={refs.ourRole}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.OUR_ROLE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={ONLINE_PRIVACY_STATEMENT.SECTIONS.OUR_ROLE.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      <SectionContent
        id='ourRoleAdditional' // Added ID
        ref={refs.ourRoleAdditional} // Added ref
        initialSpacing={false}
        linkMappings={ONLINE_PRIVACY_STATEMENT.SECTIONS.OUR_ROLE.LINKS.map(
          link => ({
            word: link.TEXT,
            url: link.URL,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          })
        )}
        text={ONLINE_PRIVACY_STATEMENT.SECTIONS.OUR_ROLE.ADDITIONAL_NOTES.map(
          note => ({
            text: note,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Data Protection Principles section */}
      <SectionContent
        id='dataProtection'
        ref={refs.dataProtection}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.DATA_PROTECTION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.DATA_PROTECTION.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          variant='blue'
          items={ONLINE_PRIVACY_STATEMENT.SECTIONS.DATA_PROTECTION.LIST_ITEMS}
        />
      </Box>

      <SectionContent
        id='dataProtectionConclusion' // Added ID
        ref={refs.dataProtectionConclusion} // Added ref
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.DATA_PROTECTION.CONCLUSION,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* What Personal Information Do We Collect section */}
      <SectionContent
        id='personalInfoCollection'
        ref={refs.personalInfoCollection}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.PERSONAL_INFO_COLLECTION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.PERSONAL_INFO_COLLECTION
              .INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={
            ONLINE_PRIVACY_STATEMENT.SECTIONS.PERSONAL_INFO_COLLECTION
              .LIST_ITEMS
          }
        />
      </Box>

      <SectionContent
        id='personalInfoSpecial' // Added ID
        ref={refs.personalInfoSpecial} // Added ref
        initialSpacing={false}
        text={ONLINE_PRIVACY_STATEMENT.SECTIONS.PERSONAL_INFO_COLLECTION.SPECIAL_CATEGORY.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* How We Collect Your Personal Information section */}
      <SectionContent
        id='infoCollectionMethod'
        ref={refs.infoCollectionMethod}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_COLLECTION_METHOD.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_COLLECTION_METHOD
              .INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={
            ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_COLLECTION_METHOD.LIST_ITEMS
          }
        />
      </Box>

      {/* How Will We Use Your Personal Information section */}
      <SectionContent
        id='infoUsage'
        ref={refs.infoUsage}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_USAGE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_USAGE.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_USAGE.LIST_ITEMS}
        />
      </Box>

      <SectionContent
        id='infoUsageSpecial' // Added ID
        ref={refs.infoUsageSpecial} // Added ref
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_USAGE
              .SPECIAL_CATEGORY_NOTE,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Automated Decision-Making section */}
      <SectionContent
        id='automatedDecision'
        ref={refs.automatedDecision}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.AUTOMATED_DECISION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={ONLINE_PRIVACY_STATEMENT.SECTIONS.AUTOMATED_DECISION.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Who Do We Share Your Personal Information With section */}
      <SectionContent
        id='infoSharing'
        ref={refs.infoSharing}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_SHARING.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_SHARING.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_SHARING.LIST_ITEMS}
        />
      </Box>

      <SectionContent
        id='infoSharingConclusion' // Added ID
        ref={refs.infoSharingConclusion} // Added ref
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_SHARING.CONCLUSION,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* International Transfers of Your Personal Information section */}
      <SectionContent
        id='internationalTransfers'
        ref={refs.internationalTransfers}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.INTERNATIONAL_TRANSFERS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={ONLINE_PRIVACY_STATEMENT.SECTIONS.INTERNATIONAL_TRANSFERS.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* How Long Do We Keep Your Personal Information section */}
      <SectionContent
        id='infoRetention'
        ref={refs.infoRetention}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_RETENTION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={ONLINE_PRIVACY_STATEMENT.SECTIONS.INFO_RETENTION.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Your Rights section */}
      <SectionContent
        id='userRights'
        ref={refs.userRights}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.USER_RIGHTS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.USER_RIGHTS.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          variant='blue'
          items={ONLINE_PRIVACY_STATEMENT.SECTIONS.USER_RIGHTS.LIST_ITEMS}
        />
      </Box>

      <SectionContent
        id='userRightsContact' // Added ID
        ref={refs.userRightsContact} // Added ref
        initialSpacing={false}
        linkMappings={ONLINE_PRIVACY_STATEMENT.SECTIONS.USER_RIGHTS.LINKS.map(
          link => ({
            word: link.TEXT,
            url: link.URL,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          })
        )}
        text={[
          {
            text: ONLINE_PRIVACY_STATEMENT.SECTIONS.USER_RIGHTS.CONTACT_INFO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Changes to This Notice section */}
      <SectionContent
        id='noticeChanges'
        ref={refs.noticeChanges}
        title={ONLINE_PRIVACY_STATEMENT.SECTIONS.NOTICE_CHANGES.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={ONLINE_PRIVACY_STATEMENT.SECTIONS.NOTICE_CHANGES.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {ONLINE_PRIVACY_STATEMENT.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default OnlinePrivacyStatementPage;
